/**
 * scene3d — shared 3D projection and drawing helpers for the
 * Chapter 1 blueprint scenes. Orthographic projection with a soft
 * perspective fake, Y-axis rotation, fixed X-axis tilt. Depth drives
 * stroke alpha, dot size, and label opacity so everything reads as
 * layered space instead of flat.
 *
 * Scenes just declare 3D points (and optional edges / labels) and
 * call drawFrame once per rAF. The helpers handle the projection,
 * depth sorting, and chrome (dot → halo → glow → label).
 */
import { ALPHA, AMBER_RGB, CREAM_RGB, CYAN_RGB } from "./designTokens";

export type Warmth = "cream" | "amber" | "cool";

export interface Point3D {
  x: number;
  y: number;
  z: number;
}

export interface Projected {
  x: number;
  y: number;
  z: number;
  scale: number;
  depthT: number;
}

export interface CameraOpts {
  cx: number;
  cy: number;
  scale: number;
  rotY: number;
  tiltX: number;
  perspective?: number;
}

export const WARMTH_RGB: Record<Warmth, string> = {
  cream: CREAM_RGB,
  amber: AMBER_RGB,
  cool: CYAN_RGB,
};

const ISOMETRIC_TILT = Math.atan(1 / Math.sqrt(2));
export const DEFAULT_TILT = ISOMETRIC_TILT * 0.78;

export function project(p: Point3D, c: CameraOpts): Projected {
  const cosY = Math.cos(c.rotY);
  const sinY = Math.sin(c.rotY);
  const cosTX = Math.cos(c.tiltX);
  const sinTX = Math.sin(c.tiltX);

  const x1 = p.x * cosY + p.z * sinY;
  const z1 = -p.x * sinY + p.z * cosY;

  const y2 = p.y * cosTX - z1 * sinTX;
  const z2 = p.y * sinTX + z1 * cosTX;

  const persp = c.perspective ?? 0.0;
  const depthT = Math.max(0, Math.min(1, 0.5 - z2 * 0.5));
  const scale = 1 - z2 * persp;

  return {
    x: c.cx + x1 * c.scale * scale,
    y: c.cy + y2 * c.scale * scale,
    z: z2,
    scale,
    depthT,
  };
}

// ──────────────────────────────────────────────────────────────────────
// Drawing helpers
// ──────────────────────────────────────────────────────────────────────

export function clear(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.clearRect(0, 0, w, h);
}

export function drawDot(
  ctx: CanvasRenderingContext2D,
  p: Projected,
  opts: {
    radius?: number;
    warmth?: Warmth;
    alpha?: number;
    halo?: number;
    /** Optional explicit RGB. When provided, overrides the warmth lookup
     *  so dots can carry arbitrary colors (e.g., wave-colored nodes). */
    rgb?: readonly [number, number, number];
  }
): void {
  const { radius = 2.2, warmth = "cream", alpha = 0.9, halo = 0 } = opts;
  const rgbArr: readonly [number, number, number] = opts.rgb
    ? [opts.rgb[0], opts.rgb[1], opts.rgb[2]]
    : (() => {
        const parts = WARMTH_RGB[warmth].split(",").map((n) => parseInt(n.trim(), 10));
        return [parts[0] ?? 242, parts[1] ?? 238, parts[2] ?? 228] as const;
      })();
  const rgb = `${Math.round(rgbArr[0])}, ${Math.round(rgbArr[1])}, ${Math.round(rgbArr[2])}`;

  // Depth shading — far orbs smaller + cooler, close orbs richer + slightly warmer.
  const depthFactor = 0.5 + p.depthT * 0.8;
  const r = radius * depthFactor;
  const a = alpha * (0.55 + p.depthT * 0.5);

  // ── Pass 1 · Far atmospheric bloom ───────────────────────────────
  // Very wide, very faint halo so the orb sits in atmosphere —
  // catches the eye before the core even registers. This is what
  // makes bright dots feel like sources of light, not stickers.
  if (halo > 0) {
    const bloomR = r * 11;
    const bloomAlpha = halo * p.depthT * 0.22;
    const bloom = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, bloomR);
    bloom.addColorStop(0, `rgba(${rgb}, ${bloomAlpha})`);
    bloom.addColorStop(0.28, `rgba(${rgb}, ${bloomAlpha * 0.55})`);
    bloom.addColorStop(0.65, `rgba(${rgb}, ${bloomAlpha * 0.15})`);
    bloom.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = bloom;
    ctx.beginPath();
    ctx.arc(p.x, p.y, bloomR, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Pass 2 · Mid halo (glow zone) ─────────────────────────────────
  // Tighter halo that is the classic "glow" — still soft, but with
  // multiple gradient stops so the falloff curves like real bloom.
  if (halo > 0) {
    const midR = r * 5.2;
    const midAlpha = halo * p.depthT;
    const mid = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, midR);
    mid.addColorStop(0, `rgba(${rgb}, ${midAlpha * 0.85})`);
    mid.addColorStop(0.32, `rgba(${rgb}, ${midAlpha * 0.45})`);
    mid.addColorStop(0.7, `rgba(${rgb}, ${midAlpha * 0.12})`);
    mid.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = mid;
    ctx.beginPath();
    ctx.arc(p.x, p.y, midR, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Pass 3 · Inner radiant glow ──────────────────────────────────
  // Tight hot zone hugging the core, blending core color outward so
  // the edge of the body fades seamlessly into the halo instead of
  // reading as a hard-edged circle on a black background.
  const innerR = r * 2.3;
  const innerAlpha = a * 0.6;
  const inner = ctx.createRadialGradient(p.x, p.y, r * 0.4, p.x, p.y, innerR);
  inner.addColorStop(0, `rgba(${rgb}, ${innerAlpha})`);
  inner.addColorStop(0.55, `rgba(${rgb}, ${innerAlpha * 0.35})`);
  inner.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.fillStyle = inner;
  ctx.beginPath();
  ctx.arc(p.x, p.y, innerR, 0, Math.PI * 2);
  ctx.fill();

  // ── Pass 4 · The body with spherical shading ─────────────────────
  // Radial gradient with an OFFSET origin (upper-left) produces a
  // specular highlight that reads as a lit sphere — like a pearl
  // catching light from the upper left. Core blends from near-white
  // hot center → body color → slightly darker rim for roundness.
  // Lightened tint for the highlight end (pulls toward white so the
  // spec feels illuminated, not just transparent).
  const litR = Math.round(Math.min(255, rgbArr[0] + (255 - rgbArr[0]) * 0.75));
  const litG = Math.round(Math.min(255, rgbArr[1] + (255 - rgbArr[1]) * 0.75));
  const litB = Math.round(Math.min(255, rgbArr[2] + (255 - rgbArr[2]) * 0.75));
  // Rim color: slightly darker for 3D falloff (70% of body).
  const rimR = Math.round(rgbArr[0] * 0.75);
  const rimG = Math.round(rgbArr[1] * 0.75);
  const rimB = Math.round(rgbArr[2] * 0.75);
  const body = ctx.createRadialGradient(
    p.x - r * 0.32,
    p.y - r * 0.36,
    0,
    p.x,
    p.y,
    r * 1.05
  );
  body.addColorStop(0, `rgba(${litR}, ${litG}, ${litB}, ${Math.min(1, a * 1.05)})`);
  body.addColorStop(0.35, `rgba(${rgb}, ${a})`);
  body.addColorStop(1, `rgba(${rimR}, ${rimG}, ${rimB}, ${a * 0.88})`);
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fill();

  // ── Pass 5 · Specular pinpoint ───────────────────────────────────
  // Tiny hot white spot up-left of the body gives the orb that "wet"
  // lit-sphere look. Only visible when the body is clearly opaque so
  // ambient/parked dots don't get an unnecessary highlight.
  if (a > 0.35) {
    const specR = r * 0.32;
    const spec = ctx.createRadialGradient(
      p.x - r * 0.38,
      p.y - r * 0.42,
      0,
      p.x - r * 0.38,
      p.y - r * 0.42,
      specR
    );
    spec.addColorStop(0, `rgba(255, 255, 255, ${a * 0.55})`);
    spec.addColorStop(0.6, `rgba(255, 255, 255, ${a * 0.12})`);
    spec.addColorStop(1, `rgba(255, 255, 255, 0)`);
    ctx.fillStyle = spec;
    ctx.beginPath();
    ctx.arc(p.x - r * 0.38, p.y - r * 0.42, specR, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function drawLine(
  ctx: CanvasRenderingContext2D,
  a: Projected,
  b: Projected,
  opts: { warmth?: Warmth; alpha?: number; width?: number }
): void {
  const { warmth = "cream", alpha = 0.35, width = 1 } = opts;
  const rgb = WARMTH_RGB[warmth];
  const depthBlend = (a.depthT + b.depthT) * 0.5;
  const finalAlpha = alpha * (0.35 + depthBlend * 0.8);
  const finalWidth = width * (0.6 + depthBlend * 0.6);
  ctx.strokeStyle = `rgba(${rgb}, ${finalAlpha})`;
  ctx.lineWidth = finalWidth;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
}

export function drawLabel(
  ctx: CanvasRenderingContext2D,
  p: Projected,
  text: string,
  opts: { offsetX?: number; offsetY?: number; warmth?: Warmth; alpha?: number; size?: number; align?: CanvasTextAlign }
): void {
  const { offsetX = 0, offsetY = -14, warmth = "cream", alpha = ALPHA.labelMicro, size = 10, align = "center" } = opts;
  const rgb = WARMTH_RGB[warmth];
  ctx.fillStyle = `rgba(${rgb}, ${alpha * (0.6 + p.depthT * 0.4)})`;
  ctx.font = `${size}px "IBM Plex Mono", ui-monospace, monospace`;
  ctx.textAlign = align;
  ctx.letterSpacing = "2px";
  ctx.fillText(text, p.x + offsetX, p.y + offsetY);
}

/** Draws a faint isometric floor grid — gives every scene a sense of ground. */
export function drawFloorGrid(
  ctx: CanvasRenderingContext2D,
  camera: CameraOpts,
  opts: { extent?: number; divisions?: number; alpha?: number } = {}
): void {
  const { extent = 1.6, divisions = 12, alpha = 0.05 } = opts;
  const step = (extent * 2) / divisions;
  ctx.strokeStyle = `rgba(${CREAM_RGB}, ${alpha})`;
  ctx.lineWidth = 0.75;
  for (let i = 0; i <= divisions; i++) {
    const d = -extent + i * step;
    const a = project({ x: d, y: 0, z: -extent }, camera);
    const b = project({ x: d, y: 0, z: extent }, camera);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    const c = project({ x: -extent, y: 0, z: d }, camera);
    const e = project({ x: extent, y: 0, z: d }, camera);
    ctx.beginPath();
    ctx.moveTo(c.x, c.y);
    ctx.lineTo(e.x, e.y);
    ctx.stroke();
  }
}

// ──────────────────────────────────────────────────────────────────────
// Animation helpers
// ──────────────────────────────────────────────────────────────────────

export function easeOut(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - x, 3);
}

export function easeInOut(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

/** 0 at t<start, easeOut from start to start+dur, 1 after. */
export function emergeT(time: number, start: number, dur: number): number {
  if (time <= start) return 0;
  if (time >= start + dur) return 1;
  return easeOut((time - start) / dur);
}
